import Button from '@atlaskit/button';
import Checkbox from '@atlaskit/checkbox';
import styled from '@emotion/styled';
import { CheckboxField, ErrorMessage, Field } from '@atlaskit/form';
import CopyIcon from '@atlaskit/icon/glyph/copy';
import AddIcon from '@atlaskit/icon/glyph/editor/add';
import LessIcon from '@atlaskit/icon/glyph/editor/divider';
import { PanelStateless } from '@atlaskit/panel';
import SectionMessage from '@atlaskit/section-message';
import { ValueType } from '@atlaskit/select';
import Textfield from '@atlaskit/textfield';
import Tooltip, { TooltipPrimitive } from '@atlaskit/tooltip';
import {
  FormattedDate,
  Icon,
  InfoPanel,
  InvoiceGroupFormField,
  InvoiceGroupSelectItem,
  isDiscretionaryDiscountPromotion,
  SummarySectionV2,
  useFeatureToggle,
} from '@css-xp/base';
import {
  EntitlementDetails,
  EntitlementTermDetails,
  InvoiceGroup,
  LinkedDestinationEntitlement,
  Maybe,
} from '@css-xp/graphtypes/src/generated-types-new';
import { getBillEstimate, getOrderPreview } from '@atlassian/temp-proxies';
import { LinkedEntitlementsInstance, NEW_DATE_FORMAT, TransitionTime } from '@atlassian/cssxp-typescommon';
import { isFreePlanEntitlement } from '@atlassian/cssxp-utils-entitlement';
import {
  ENABLE_ANNUAL_TERMS,
  ENABLE_GLP_BILL_ESTIMATION,
  ENABLE_ORDER_BILL_ESTIMATION,
} from '@atlassian/cssxp-utils-generic';
import { isStatuspage } from '@atlassian/cssxp-utils-product';
import defaultTo from 'lodash/defaultTo';
import noop from 'lodash/noop';
import moment, { Moment } from 'moment';
import React, { useMemo } from 'react';
import { validateFreeSubscriptions } from '../../../../../entitlement-page/src/components/entitlement-page-utils';
import { getCustomPromotionInstance } from '../../../utils/promotion-utils';
import { getOrderIntentPayload } from '../manage-entitlement-helper';
import { FormState, OrderPreviewData, CoBillData, Estimate, DiscretionaryEstimate } from '../manage-entitlement-v2';
import '../manage-entitlement-v2.scss';
import { POUpdate } from '../utils/manage-po-update';
import { copyReviewChangeContentV2 } from './copy-review-change-content-v2';
import { PriceDifference } from './price-difference';
import { ProductFamilyTableV2 } from './product-family-table-v2';
import { formatOrderPreviewResponse, validateOrderPreviewDetails } from './review-change-helper';

interface Props {
  entitlement: EntitlementDetails;
  formState: FormState;
  availableInvoiceGroups: InvoiceGroup[] | never[];
  allAvailableInvoiceGroups?: InvoiceGroup[] | never[];
  updateFormState: (data: FormState) => void;
  onDiscretionaryEstimateChange: (obj: DiscretionaryEstimate) => void;
  linkedEntitlementInvoiceGroupId: Maybe<string> | undefined;
  linkedEntitlements: LinkedDestinationEntitlement[];
  isBillingCycleChanged: boolean;
  setGrandTotal: (value: number) => void;
  isMonthlySelected: boolean;
  isAnnualTermedSelected: boolean;
  setOrderPreviewdata: (data: OrderPreviewData) => void;
  orderPreviewData: OrderPreviewData;
  isAnnualSelected: boolean;
  currentDate: Moment;
  coBillData: CoBillData;
  estimatedBill: Estimate;
  setEstimatedBill: (data: Estimate) => void;
  creditDetails: EntitlementTermDetails | null | undefined;
}

const renderCoBillTooltip = () => {
  return styled(TooltipPrimitive)`
    padding: 8px 12px;
    background: #172b4d;
    border-radius: 3px;
    color: #ffffff;
    font-size: 12px;
    white-space: pre;
  `;
};

export const ReviewChangeV2 = ({
  entitlement,
  formState,
  availableInvoiceGroups,
  allAvailableInvoiceGroups,
  updateFormState,
  onDiscretionaryEstimateChange,
  linkedEntitlementInvoiceGroupId,
  linkedEntitlements = [],
  isBillingCycleChanged,
  setGrandTotal,
  isMonthlySelected,
  isAnnualTermedSelected,
  setOrderPreviewdata,
  orderPreviewData,
  isAnnualSelected,
  currentDate,
  coBillData,
  estimatedBill,
  setEstimatedBill,
  creditDetails,
}: Props) => {
  const [panelState, setPanelState] = React.useState<boolean>(true);
  const [productsPanelState, setProductsPanelState] = React.useState<boolean>(true);
  const [canUpdatePO, setCanUpdatePO] = React.useState<boolean>(false);
  const [makeImmediate, setMakeImmediate] = React.useState<boolean>(false);
  const [isEnabled] = useFeatureToggle();
  const isBillEsimationEnabled = isEnabled(ENABLE_ORDER_BILL_ESTIMATION);
  const isAnnualTermEnabled = isEnabled(ENABLE_ANNUAL_TERMS);
  const isStatuspageProduct = isStatuspage(entitlement.productId);
  const isCustomerConsentRequired = isAnnualTermEnabled
    ? isAnnualSelected
    : (isBillingCycleChanged || isFreePlanEntitlement(entitlement)) && isAnnualSelected;
  const isGLPEstimationEnabled = isEnabled(ENABLE_GLP_BILL_ESTIMATION);
  const discreteDiscountPromotions = useMemo(() => {
    const promotions = [];
    const promotionKeys = Object.keys(formState).filter((key) => key.trim().includes('promotionId'));
    for (let i = 0; i < promotionKeys.length; i++) {
      if (isDiscretionaryDiscountPromotion(formState, promotionKeys[i])) {
        promotions.push(getCustomPromotionInstance(i, formState));
      }
    }
    return promotions;
  }, [formState]);

  const startDate = moment.utc(currentDate).valueOf();

  const fetchOrderPreview = async () => {
    if (!isStatuspageProduct) {
      try {
        setOrderPreviewdata({
          loading: true,
        });

        const orderIntentPayload = getOrderIntentPayload({
          entitlement,
          formState,
          linkedEntitlements,
          isBillingCycleChanged,
          makeImmediate,
        });
        const response: OrderPreviewData['response'] = await getOrderPreview(orderIntentPayload);
        const formattedOrderPreviewResponse = formatOrderPreviewResponse(
          response,
          formState,
          entitlement,
          linkedEntitlements
        );
        const updatedOrderPreviewState: OrderPreviewData = {
          loading: false,
          response: formattedOrderPreviewResponse,
        };
        const error = validateOrderPreviewDetails(formattedOrderPreviewResponse);

        if (error) {
          updatedOrderPreviewState.error = {
            message: error,
          };
        }

        setOrderPreviewdata(updatedOrderPreviewState);
      } catch (error) {
        setOrderPreviewdata({
          loading: false,
          error,
        });
      }
    }
  };

  const fetchBillEstimate = async (isImmediate?: boolean) => {
    if (isBillEsimationEnabled) {
      try {
        let orderIntentPayload = getOrderIntentPayload({
          entitlement,
          formState,
          linkedEntitlements,
          isBillingCycleChanged,
          makeImmediate: isImmediate ?? makeImmediate,
        });

        if (isBillingCycleChanged || isAnnualSelected) {
          orderIntentPayload = {
            ...orderIntentPayload,
            skipTrial: formState.skipTrialEligible ? true : undefined,
            linkedEntitlements: orderIntentPayload.linkedEntitlements?.map((item, index) => {
              const linkedEntitlementFormState = formState[item.entitlementId as keyof FormState] as FormState;
              const updatedItem = {
                ...item,
              };

              // first item is the current entitlement so ignoring first index as setting true at root level for it
              if (index !== 0) {
                updatedItem.skipTrial = linkedEntitlementFormState?.skipTrialEligible ? true : undefined;
              }

              return updatedItem;
            }) as LinkedEntitlementsInstance[],
          };
        }

        setEstimatedBill({ loading: true });
        const response = await getBillEstimate(orderIntentPayload);
        setEstimatedBill({ response, loading: false });

        onDiscretionaryEstimateChange({
          discretionaryDiscountAmount: defaultTo(response?.discretionaryDiscountAmount, 0),
          loading: false,
        });
      } catch (error) {
        onDiscretionaryEstimateChange({ error, loading: false });
        setEstimatedBill({ error, loading: false });
      }
    }
  };

  React.useEffect(() => {
    if (!isAnnualTermEnabled) {
      fetchOrderPreview();
      fetchBillEstimate();
    }
  }, [formState?.invoiceGroupId?.value, makeImmediate]);

  React.useEffect(() => {
    isAnnualTermEnabled && fetchOrderPreview();
  }, [makeImmediate]);

  React.useEffect(() => {
    if (!isAnnualTermEnabled && !entitlement?.billingGroupId && linkedEntitlementInvoiceGroupId) {
      updateFormState({
        ...formState,
        invoiceGroupId: {
          invoiceGroup: {},
          value: linkedEntitlementInvoiceGroupId as string,
        },
      });
    }
  }, [linkedEntitlementInvoiceGroupId]);

  React.useEffect(() => {
    if (!isBillEsimationEnabled && discreteDiscountPromotions.length) {
      onDiscretionaryEstimateChange({ isDisabled: true, loading: false });
    }
  }, [isBillEsimationEnabled, discreteDiscountPromotions]);

  const handleChange = () => {
    setPanelState(!panelState);
  };
  const handleChangeProductDetails = () => {
    setProductsPanelState(!productsPanelState);
  };

  const currentInvoiceGroup = defaultTo(availableInvoiceGroups, []).find(
    (ig) => ig?.id === entitlement?.billingGroupId
  );
  const shipToPartyId = defaultTo(currentInvoiceGroup?.shipToParty?.id, undefined);

  const renderPriceDifference = () => {
    return (
      <PriceDifference
        currentEstimatedBill={entitlement?.nextBillEstimate?.total || 0}
        estimatedBill={estimatedBill}
        setGrandTotal={setGrandTotal}
        isBillingCycleChanged={isBillingCycleChanged}
        entitlement={entitlement}
      />
    );
  };

  const disableMakeImmediateCheckbox = useMemo(() => {
    return !orderPreviewData?.response?.items.some(
      (item) => item?.processingInfo?.transitionTime === TransitionTime.SCHEDULED
    );
  }, [orderPreviewData]);

  let purchaseLabel = 'Monthly';
  let cobillLabel = 'billed';
  if (isAnnualSelected && !isAnnualTermedSelected) {
    purchaseLabel = 'Auto renew';
  } else if (isAnnualTermedSelected) {
    purchaseLabel = 'Termed';
    cobillLabel = 'termed';
  }

  let coBillSubscriptions;
  let endTimestampValue = formState?.endTimestamp;
  let anchorTime = formState?.nextBillingAnchorTimestamp;

  if (formState.coBillTimestamp) {
    if (endTimestampValue) {
      endTimestampValue = formState.coBillTimestamp;
    } else {
      anchorTime = formState.coBillTimestamp as number;
    }

    coBillSubscriptions = coBillData?.response?.find(
      (item) => item.billingDate === moment.utc(formState.coBillTimestamp).format(NEW_DATE_FORMAT)
    )?.entitlements;
  }

  const shouldRenderNextBillDate = !isStatuspageProduct && anchorTime && !endTimestampValue;
  const showDuration = !formState.coBillTimestamp && isBillingCycleChanged && isAnnualSelected && !isStatuspageProduct;
  const showOptedForUpgradeTrialBanner = useMemo(() => {
    return (
      !!formState.hasOptedForUpgradeTrial ||
      linkedEntitlements.some((item) => !!formState[item.entitlementId]?.hasOptedForUpgradeTrial)
    );
  }, [linkedEntitlements, formState]);

  return (
    <div className="review-change-v2">
      <h2 onClick={handleChange}>Billing cycle details</h2>
      <span onClick={handleChange} className="header-icon">
        {panelState ? (
          <LessIcon testId="more-icon" label="more-icon" />
        ) : (
          <AddIcon testId="add-icon" label="add-icon" />
        )}
      </span>
      <PanelStateless header={null} isExpanded={panelState} onChange={noop}>
        <div className="billing-details">
          <SummarySectionV2.SummarySection
            testId="summary"
            className="summary-section-wrapper"
            columns="repeat(auto-fit, minmax(120px, 1fr))"
          >
            <SummarySectionV2.SummaryGridCalloutItem>
              <div>
                <label>Purchase</label>
                <div>{purchaseLabel}</div>
              </div>
            </SummarySectionV2.SummaryGridCalloutItem>
            {showDuration && (
              <SummarySectionV2.SummaryGridCalloutItem>
                <div>
                  <label>Billing period</label>
                  <div>{isAnnualTermedSelected ? formState?.termLength?.label : '12 months'}</div>
                </div>
              </SummarySectionV2.SummaryGridCalloutItem>
            )}
            <SummarySectionV2.SummaryGridCalloutItem>
              <div>
                <label>Start date</label>
                <div>
                  <FormattedDate date={startDate.toString()} format={NEW_DATE_FORMAT} />
                </div>
              </div>
            </SummarySectionV2.SummaryGridCalloutItem>
            {shouldRenderNextBillDate && (
              <SummarySectionV2.SummaryGridCalloutItem>
                <div>
                  <label>Bill anchor date</label>
                  <div data-testid="billAnchorDate">
                    {moment.utc(anchorTime).format(isMonthlySelected ? 'DD' : 'MMM DD')}
                  </div>
                </div>
              </SummarySectionV2.SummaryGridCalloutItem>
            )}
            {endTimestampValue && (
              <SummarySectionV2.SummaryGridCalloutItem>
                <div>
                  <label>End date</label>
                  <div data-testid="endDate">
                    <FormattedDate date={endTimestampValue.toString()} format={NEW_DATE_FORMAT} />
                  </div>
                </div>
              </SummarySectionV2.SummaryGridCalloutItem>
            )}
            {formState?.coBillTimestamp && (
              <SummarySectionV2.SummaryGridCalloutItem styles={{ gridColumn: 1 }}>
                <div>
                  <label>{`Co-${cobillLabel} with`}</label>
                  <div data-testid="coBillEnt" className="cobill-entitlement-name">
                    {coBillSubscriptions?.[0]?.productName} {coBillSubscriptions?.[0]?.entitlementNumber}
                  </div>
                  {coBillSubscriptions && coBillSubscriptions?.length > 1 && (
                    <div>
                      <Tooltip
                        component={renderCoBillTooltip()}
                        content={coBillSubscriptions?.map((item, index) => (
                          <div key={item?.id} data-testid={`coBill-item${index}`}>
                            {item?.productName} {item?.entitlementNumber}
                            {index !== coBillSubscriptions?.length - 1 ? ',' : ''}
                          </div>
                        ))}
                      >
                        <span data-testid="coBillTooltip" className="cobill-view-more">
                          view more
                        </span>
                      </Tooltip>
                    </div>
                  )}
                </div>
              </SummarySectionV2.SummaryGridCalloutItem>
            )}
          </SummarySectionV2.SummarySection>
        </div>
      </PanelStateless>
      {isAnnualTermEnabled && !isStatuspageProduct && (
        <div className="immediate-amendment-checkbox">
          <CheckboxField name="immediateAmendment" defaultIsChecked={makeImmediate}>
            {({ fieldProps }) => (
              <>
                <Checkbox
                  {...fieldProps}
                  id="immediate-amendment-checkbox"
                  testId="immediate-amendment-checkbox"
                  isDisabled={orderPreviewData.loading || disableMakeImmediateCheckbox}
                  onChange={(e) => {
                    fieldProps.onChange(e);
                    setMakeImmediate(!makeImmediate);
                    isAnnualTermEnabled && fetchBillEstimate(!makeImmediate);
                  }}
                />
              </>
            )}
          </CheckboxField>
          <span>Override scheduling and make immediate</span>
        </div>
      )}
      <h2 onClick={handleChangeProductDetails}>Subscription details</h2>
      <span onClick={handleChangeProductDetails} className="product-family-icon">
        {productsPanelState ? (
          <LessIcon testId="more-icon" label="more-icon" />
        ) : (
          <AddIcon testId="add-icon" label="add-icon" />
        )}
      </span>
      <PanelStateless header={null} isExpanded={productsPanelState} onChange={noop}>
        <ProductFamilyTableV2
          entitlement={entitlement}
          linkedEntitlements={linkedEntitlements}
          estimatedBill={estimatedBill}
          formState={formState}
          isBillingCycleChanged={isBillingCycleChanged}
          isMonthlySelected={isMonthlySelected}
          orderPreviewData={orderPreviewData}
          showBillEstimateWarning={true}
        />
      </PanelStateless>
      {isBillEsimationEnabled && renderPriceDifference()}
      {isCustomerConsentRequired && (
        <div className="consent-info-container">
          <InfoPanel
            type="warning"
            title="Customer consent is required"
            message={
              <p>
                Changes made to this subscription will require customer consent to be recorded. Use the button below to
                copy the details to your customer’s GSAC ticket.
                <br />
                <br />
                If you’ve already received consent, enter the GSAC ticket details in the Support ticket link field
                below.
              </p>
            }
          />
          <Button
            iconBefore={<CopyIcon label="" size="medium" />}
            isDisabled={estimatedBill.loading || orderPreviewData.loading}
            onClick={() =>
              copyReviewChangeContentV2({
                entitlement,
                formState,
                isBillingCycleChanged,
                estimatedBill,
                linkedEntitlements,
                anchorTime,
                showDuration,
                availableInvoiceGroups: allAvailableInvoiceGroups,
                orderPreviewData,
                isAnnualTermedSelected,
                isGLPEstimationEnabled,
                startDate,
                endTimestampValue,
              })
            }
            appearance="primary"
          >
            Copy details for approval
          </Button>
        </div>
      )}
      {showOptedForUpgradeTrialBanner && (
        <div className="upgrade-trial-info-banner">
          <SectionMessage appearance="information" testId="upgrade-trial-info-banner">
            <p>
              The customer will be moved to a no-cost trial for the Premium plan but will be charged for their current
              plan.
            </p>
          </SectionMessage>
        </div>
      )}
      <h2>Submit changes</h2>
      {isAnnualTermEnabled ? (
        <InvoiceGroupFormField
          isRequired
          isDisabled
          availableInvoiceGroups={allAvailableInvoiceGroups}
          creditDetails={creditDetails}
          entitlementBillingInvoiceGroupId={formState?.invoiceGroupId?.value}
          purchaseOrder={{
            number: formState.ponumber,
            oneTimeUse: !formState.isMultiUse,
          }}
          fieldLabel="Billing profile"
        />
      ) : (
        <>
          <InvoiceGroupFormField
            isRequired
            isDisabled={!validateFreeSubscriptions(entitlement) || availableInvoiceGroups?.length === 1}
            availableInvoiceGroups={availableInvoiceGroups}
            entitlementBillingInvoiceGroupId={formState?.invoiceGroupId?.value}
            fieldLabel="Billing profile"
            creditDetails={creditDetails}
            onChange={(value: ValueType<InvoiceGroupSelectItem, false> | React.FormEvent<HTMLSelectElement>) => {
              updateFormState({
                ...formState,
                invoiceGroupId: value as InvoiceGroupSelectItem,
              });
            }}
          />
          {!canUpdatePO && (
            <Button
              testId="add-po-button"
              onClick={() => setCanUpdatePO(true)}
              iconBefore={<AddIcon label="add" />}
              appearance="link"
            >
              Update PO number
            </Button>
          )}
          {canUpdatePO && (
            <POUpdate
              setCanUpdatePO={setCanUpdatePO}
              billingGroupId={formState?.invoiceGroupId?.value}
              entitlement={entitlement}
              shipToPartyId={shipToPartyId}
            />
          )}
        </>
      )}

      <Field
        label={'Support ticket number'}
        name="supportTicketLink"
        isRequired
        validate={(value: any) => (value && value.length > 0 ? undefined : 'INVALID')}
      >
        {({ fieldProps, error }) => (
          <>
            <Textfield
              className="support-ticket"
              {...fieldProps}
              name={'supportTicketLink'}
              testId="supportTicketLink-field"
              placeholder={'Add GSAC ticket number'}
            />
            {error && <ErrorMessage testId="supportTicketLink-error">Enter a valid ticket number</ErrorMessage>}
          </>
        )}
      </Field>
      {isCustomerConsentRequired && (
        <div className="customer-consent-field">
          <Field
            name="consentTaken"
            isRequired
            validate={(value: any) => (!value ? 'Please confirm customer consent' : undefined)}
          >
            {({ fieldProps, error }) => (
              <>
                <Checkbox
                  {...fieldProps}
                  defaultChecked={!!formState?.consentTaken}
                  label="I confirm customer consent has been received"
                  testId="consent-taken-checkbox"
                />
                {error && <ErrorMessage testId="customer-consent-error">{error}</ErrorMessage>}
              </>
            )}
          </Field>
        </div>
      )}
      <Field
        label={'Internal comment'}
        name="notes"
        isRequired
        validate={(value: any) => (value && value.length > 0 ? undefined : 'INVALID')}
      >
        {({ fieldProps, error }) => (
          <>
            <Textfield
              className="internal-notes"
              {...fieldProps}
              name={'notes'}
              testId="notes-field"
              placeholder={'Add order comments'}
            />
            {error && <ErrorMessage testId="supportTicketLink-error">This field is required</ErrorMessage>}
          </>
        )}
      </Field>
      {discreteDiscountPromotions.length && !isBillEsimationEnabled ? (
        <div className="approval-info-message" data-testid="bill-estimate-disabled-custom-warning">
          <Icon type="warning" />
          <span>
            We can’t show the price estimate right now. Need help? Contact <strong>#help-advocate-central</strong>{' '}
            channel.
          </span>
        </div>
      ) : null}
    </div>
  );
};
